<template>
  <v-list subheader flat>
    <div class="d-flex justify-space-between align-center pb-2">
      <div
        class="text--secondary text-capitalize font-weight-medium text-subtitle-1"
      >
        {{ title }}
        <v-btn
          v-if="managementRoles && !isMobileOrIPad && !readonly && isDesignedVr"
          color="info"
          class="mx-2"
          small
          @click="expandAll"
          >{{ showAll ? 'Collapse all' : 'Expand All' }}</v-btn
        >
        <span
          class="text-body-2 mx-3"
          :class="
            doneTasks === tasks.length ? 'success--text' : 'tertiary--text'
          "
        >
          {{ doneTasks }} / {{ tasks.length }} Done</span
        >
      </div>
      <div v-if="!isContractor && !isMobile">
        <v-menu v-if="selectedSubTasks.length" offset-y>
          <template #activator="{ on, attrs }">
            <v-btn color="darkpurple" text small v-bind="attrs" v-on="on">
              {{ $t('Actions') }}
              <v-icon class="ml-2" small>fas fa-caret-down</v-icon>
            </v-btn>
          </template>
          <v-list>
            <LtFromSubtaskModal
              :selected="selectedSubTasks"
              :listing-task-id="listingTask.id"
              @onaction="resetTasks"
            >
              <template #activator="slotData">
                <v-list-item v-on="slotData.on">
                  <v-list-item-title
                    class="d-flex align-center darkpurple--text"
                  >
                    <v-icon class="mr-1" color="darkpurple" small
                      >fas fa-tasks</v-icon
                    >{{ $t('New SC') }}</v-list-item-title
                  >
                </v-list-item>
              </template>
            </LtFromSubtaskModal>
            <v-list-item v-if="selectedSubTasks.length === 1" @click="editTask">
              <v-list-item-title class="d-flex align-center info--text">
                <v-icon color="info" class="mr-1" small>edit</v-icon>
                {{ $t('Edit') }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item @click="bulkApprove">
              <v-list-item-title class="d-flex align-center success--text">
                <v-icon color="success" class="mr-1" small
                  >mdi-check-circle</v-icon
                >
                {{ $t('Approve') }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item @click="bulkReject">
              <v-list-item-title class="d-flex align-center error--text">
                <v-icon color="error" class="mr-1" small
                  >mdi-close-circle</v-icon
                >
                {{ $t('Reject') }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item @click="bulkUndo">
              <v-list-item-title class="d-flex align-center grey--text">
                <v-icon color="grey" class="mr-1" small>fas fa-undo</v-icon>
                {{ $t('Refresh') }}
              </v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <ConfirmationModal
              text="Are you sure you want to delete those tasks ?"
              @action="deleteTask"
            >
              <template #activator="slotData">
                <v-list-item v-on="slotData.on">
                  <v-list-item-title class="d-flex align-center warning--text">
                    <v-icon small color="warning" class="mr-1">$trash</v-icon>
                    {{ $t('Delete') }}
                  </v-list-item-title>
                </v-list-item>
              </template>
            </ConfirmationModal>
          </v-list>
        </v-menu>
        <LtSubtaskCreateModal
          v-if="!isMobileOrIPad && !readonly"
          :listing-task-id="listingTask.id"
        >
          <template #activator="slotData">
            <v-btn class="white" elevation="0" small v-on="slotData.on">
              <v-icon small class="pr-1" color="darkpurple">fas fa-plus</v-icon>
              {{ $t('New') }}
            </v-btn>
          </template>
        </LtSubtaskCreateModal>
      </div>
    </div>
    <v-divider />

    <LtSubtaskRow
      v-for="task in tasks"
      :key="`${task.id}-${refreshCounter}`"
      :task="task"
      :is-expanded="showAll"
      :list-name="title"
      :listing-task="listingTask"
      :selected="selectedSubTasks.includes(task.id)"
      :readonly="readonly"
      @selected="addTask"
      @unselected="removeTask"
    />
  </v-list>
</template>

<script>
import LtSubtaskRow from 'components/listing-tasks/lt-subtask-row'
import LtSubtaskCreateModal from 'components/listing-tasks/lt-subtask-create-modal'
import ConfirmationModal from 'components/modals/confirmation-modal'
import LtFromSubtaskModal from 'components/listing-tasks/lt-from-subtask-modal'
import DeviceMixin from 'components/mixins/device-mixin'
import CommonFunctions from 'components/mixins/common_functions'
import PermissionsMixin from 'components/mixins/permissions-mixin'

export default {
  name: 'LtSubtaskList',
  components: {
    LtFromSubtaskModal,
    ConfirmationModal,
    LtSubtaskCreateModal,
    LtSubtaskRow,
  },
  mixins: [CommonFunctions, PermissionsMixin, DeviceMixin],
  props: ['tasks', 'listingTask', 'title', 'readonly'],
  data() {
    return {
      selectedSubTasks: [],
      openedTasks: false,
      showAll: false,
      editableTask: null,
      panel: [],
      refreshCounter: 0,
    }
  },
  computed: {
    doneTasks() {
      return this.tasks.filter(
        t =>
          t.is_valid !== null || t.status === 'Done' || t.status === "Can't do"
      ).length
    },
  },
  methods: {
    expandAll() {
      this.showAll = !this.showAll
      this.panel = this.tasks.map((_t, i) => i)
    },
    editTask() {
      this.$store.commit('showListingSubTaskModal', this.selectedSubTasks[0])
    },
    resetTasks() {
      this.selectedSubTasks = []
    },
    addTask(id) {
      this.selectedSubTasks = this.selectedSubTasks.concat(id)
    },
    removeTask(id) {
      this.selectedSubTasks = this.selectedSubTasks.filter(
        taskId => taskId !== id
      )
    },
    deleteTask() {
      this.$store.dispatch('deleteListingSubTask', {
        ids: this.selectedSubTasks,
        listingTaskId: this.listingTask.id,
      })
      this.selectedSubTasks = []
    },
    async bulkApprove() {
      const selectedIds = [...this.selectedSubTasks]

      const updates = selectedIds.map(taskId => {
        const task = this.tasks.find(t => t.id === taskId)
        if (task) {
          return this.$store.dispatch('updateListingSubTask', {
            id: taskId,
            ...(task.inspection_mode ? { is_valid: true } : { status: 'Done' }),
          })
        }
        return Promise.resolve()
      })

      this.resetTasks()

      try {
        await Promise.all(updates)
        await this.$nextTick()
        this.refreshCounter++
        this.$forceUpdate()
      } catch (error) {
        console.error('Error in bulkApprove:', error)
      }
    },

    async bulkReject() {
      const selectedIds = [...this.selectedSubTasks]

      const updates = selectedIds.map(taskId => {
        const task = this.tasks.find(t => t.id === taskId)
        if (task && task.inspection_mode) {
          return this.$store.dispatch('updateListingSubTask', {
            id: taskId,
            is_valid: false,
          })
        }
        return Promise.resolve()
      })

      this.resetTasks()

      try {
        await Promise.all(updates)
        await this.$nextTick()
        this.refreshCounter++
        this.$forceUpdate()
      } catch (error) {
        console.error('Error in bulkReject:', error)
      }
    },

    async bulkUndo() {
      const selectedIds = [...this.selectedSubTasks]

      const updates = selectedIds.map(taskId => {
        const task = this.tasks.find(t => t.id === taskId)
        if (task) {
          if (task.inspection_mode) {
            return this.$store.dispatch('updateListingSubTask', {
              id: taskId,
              is_valid: null,
            })
          } else if (task.status === 'Done' || task.status === "Can't do") {
            return this.$store.dispatch('updateListingSubTask', {
              id: taskId,
              status: 'New',
            })
          }
        }
        return Promise.resolve()
      })

      this.resetTasks()

      try {
        await Promise.all(updates)
        await this.$nextTick()
        this.refreshCounter++
        this.$forceUpdate()
      } catch (error) {
        console.error('Error in bulkUndo:', error)
      }
    },
  },
}
</script>

<style scoped>
.sc-task-row {
  border: 1px solid var(--v-secondary-lighten1);
}
</style>
